import { useMutation } from "@tanstack/react-query";

import * as lifeEnergyApi from "@core/apis/lifeEnergy.api";
import type { CommonUseMutationOptions } from "@core/types/app";
import type { CustomError, NoDataResponse } from "@core/types/api";
import type { LifeEnergySaveRequest, LifeEnergyUpdateRequest, LifeEnergySpendRequest } from "@core/types/lifeEnergy";
import type { Member, UpdateLifePotionRequest, UpdateLifePotionsRequest, UsePotionRequest } from "@core/types/member";

export const useUpdateLifeEnergy = <C = unknown>(
    options?: CommonUseMutationOptions<LifeEnergyUpdateRequest, NoDataResponse, CustomError, C>
) => {
    const mutation = useMutation({
        ...options,
        mutationFn: (params: LifeEnergyUpdateRequest) => lifeEnergyApi.update(params),
    });

    return mutation;
};

export const useSaveLifeEnergy = <C = unknown>(
    options?: CommonUseMutationOptions<LifeEnergySaveRequest, NoDataResponse, CustomError, C>
) => {
    const mutation = useMutation({
        ...options,
        mutationFn: (params: LifeEnergySaveRequest) => lifeEnergyApi.save(params),
    });

    return mutation;
};

export const useRemoveLifeEnergy = <C = unknown>(
    options?: CommonUseMutationOptions<string, NoDataResponse, CustomError, C>
) => {
    const mutation = useMutation({
        ...options,
        mutationFn: (characterName: string) => lifeEnergyApi.remove(characterName),
    });

    return mutation;
};

export const useSpendLifeEnergy = <C = unknown>(
    options?: CommonUseMutationOptions<LifeEnergySpendRequest, NoDataResponse, CustomError, C>
) => {
    const mutation = useMutation({
        ...options,
        mutationFn: (params: LifeEnergySpendRequest) => lifeEnergyApi.spend(params),
    });

    return mutation;
};

export const useUpdateLifePotion = <C = unknown>(
    options?: CommonUseMutationOptions<UpdateLifePotionRequest, Member, CustomError, C>
) => {
    return useMutation({
        ...options,
        mutationFn: (params: UpdateLifePotionRequest) => lifeEnergyApi.updatePotion(params),
    });
};

export const useUpdateLifePotions = <C = unknown>(
    options?: CommonUseMutationOptions<UpdateLifePotionsRequest, Member, CustomError, C>
) => {
    return useMutation({
        ...options,
        mutationFn: (params: UpdateLifePotionsRequest) => lifeEnergyApi.updatePotions(params),
    });
};

export const useUsePotion = <C = unknown>(
    options?: CommonUseMutationOptions<UsePotionRequest, Member, CustomError, C>
) => {
    return useMutation({
        ...options,
        mutationFn: (params: UsePotionRequest) => lifeEnergyApi.usePotion(params),
    });
};
